<template>
  <Modal :open="isOpen"
    :dark="dark"
    @close="$emit('close')">

    <template #header>
      <div v-if="icons?.length"
        class="modal-icons">
        <div v-for="(icon, i) in icons"
          :key="i"
          class="modal-icon">
          <i :class="'fa-solid fa-' + icon" />
        </div>
      </div>
      <h2 class="modal-title">{{ title }}</h2>
      <p v-if="description"
        class="modal-desc">{{ description }}</p>
    </template>

    <div v-if="pricing?.length"
      class="modal-pricing">
      <template v-for="(item, i) in pricing"
        :key="i">
        <div class="pricing-row">
          <span class="pricing-label">{{ item.label }}</span>
          <span class="pricing-dots" />
          <button v-if="item.allowCalculator"
            type="button"
            class="pricing-calc-btn"
            :class="{ active: calculatorOpen === `${i}` }"
            @click="toggleCalculator(`${i}`)">
            <i class="fa-solid fa-calculator" />
          </button>
          <span class="pricing-price">
            <span v-if="item.pricePrefix"
              class="pricing-affix">{{ item.pricePrefix }}</span> {{ formatPrice(item.price) }}
            €<span v-if="item.priceSuffix"
              class="pricing-affix"> {{ item.priceSuffix }}</span>
          </span>
        </div>

        <div v-if="item.allowCalculator && calculatorOpen === `${i}`"
          class="pricing-calc">
          <label class="pricing-calc__label">Kogus:</label>
          <div class="pricing-calc__input-wrap">
            <button type="button"
              class="pricing-calc__step"
              :disabled="(multipliers[`${i}`] || 1) <= 1"
              @click="setMultiplier(`${i}`, (multipliers[`${i}`] || 1) - 1)">−</button>
            <el-input class="pricing-calc__input"
              type="number"
              :model-value="multipliers[`${i}`] || 1"
              :min="1"
              @input="onMultiplierInput(`${i}`, $event)" />
            <button type="button"
              class="pricing-calc__step"
              @click="setMultiplier(`${i}`, (multipliers[`${i}`] || 1) + 1)">+</button>
          </div>
          <span class="pricing-calc__result">= {{ calcTotal(item, `${i}`) }}</span>
        </div>

        <template v-for="(child, j) in (item.children || [])"
          :key="'c' + j">
          <div class="pricing-row pricing-row--sub">
            <span class="pricing-label">{{ child.label }}</span>
            <span class="pricing-dots" />
            <button v-if="child.allowCalculator"
              type="button"
              class="pricing-calc-btn"
              :class="{ active: calculatorOpen === `${i}-${j}` }"
              @click="toggleCalculator(`${i}-${j}`)">
              <i class="fa-solid fa-calculator" />
            </button>
            <span class="pricing-price">
              <span v-if="child.pricePrefix"
                class="pricing-affix">{{ child.pricePrefix }}</span> {{ formatPrice(child.price) }}
              €<span v-if="child.priceSuffix"
                class="pricing-affix"> {{ child.priceSuffix }}</span>
            </span>
          </div>

          <div v-if="child.allowCalculator && calculatorOpen === `${i}-${j}`"
            class="pricing-calc">
            <label class="pricing-calc__label">Kogus:</label>
            <div class="pricing-calc__input-wrap">
              <button type="button"
                class="pricing-calc__step"
                :disabled="(multipliers[`${i}-${j}`] || 1) <= 1"
                @click="setMultiplier(`${i}-${j}`, (multipliers[`${i}-${j}`] || 1) - 1)">−</button>
              <el-input class="pricing-calc__input"
                type="number"
                :model-value="multipliers[`${i}-${j}`] || 1"
                :min="1"
                @input="onMultiplierInput(`${i}-${j}`, $event)" />
              <button type="button"
                class="pricing-calc__step"
                @click="setMultiplier(`${i}-${j}`, (multipliers[`${i}-${j}`] || 1) + 1)">+</button>
            </div>
            <span class="pricing-calc__result">= {{ calcTotal(child, `${i}-${j}`) }}</span>
          </div>
        </template>
      </template>
      <p class="pricing-vat">Hinnad sisaldavad käibemaksu.</p>
    </div>

    <template #footer>
      <button class="btn btn-primary"
        @click="$emit('contact')">
        <i class="fa-solid fa-envelope"
          :style="{ marginRight: '6rem' }" />
        {{ contactLabel || 'Contact Us' }}
      </button>
    </template>
  </Modal>
</template>

<script>
export default {
  name: 'service-modal',
  emits: ['close', 'contact'],
  props: {
    isOpen: Boolean,
    service: { type: Object, default: null },
    contactLabel: String,
    dark: { type: Boolean, default: false }
  },
  data() {
    return {
      cached: null,
      calculatorOpen: null,
      multipliers: {},
    }
  },
  computed: {
    s() { return this.cached || {} },
    icons() { return this.s.icon ? this.s.icon.split(',') : [] },
    title() { return this.s.title || '' },
    description() { return this.s.description || '' },
    pricing() { return (this.s.pricing || []).filter(p => p.isActive !== false) }
  },
  watch: {
    service(val) {
      if (val) this.cached = val
    },
    isOpen(val) {
      if (!val) {
        this.calculatorOpen = null
        this.multipliers = {}
      }
    }
  },
  methods: {
    toggleCalculator(key) {
      this.calculatorOpen = this.calculatorOpen === key ? null : key
    },
    setMultiplier(key, val) {
      const n = Math.max(1, Math.floor(val))
      this.multipliers = { ...this.multipliers, [key]: n }
    },
    onMultiplierInput(key, val) {
      const raw = String(val).replace(/[^0-9]/g, '')
      const n = Math.max(1, parseInt(raw, 10) || 1)
      this.setMultiplier(key, n)
    },
    calcTotal(item, key) {
      const mult = this.multipliers[key] || 1
      const numericPrice = parseFloat(item.price)
      if (isNaN(numericPrice)) return item.price
      const total = numericPrice * mult
      const formatted = total % 1 === 0 ? String(total) : total.toFixed(2).replace('.', ',')
      const suffix = item.priceSuffix ? ' ' + item.priceSuffix : ''
      return `${formatted} €${suffix}`
    },
    formatPrice(price) {
      const num = parseFloat(price)
      if (isNaN(num)) return price
      return num % 1 === 0 ? String(num) : num.toFixed(2).replace('.', ',')
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-icons {
  display: flex;
  gap: 12rem;
  justify-content: center;
  margin-bottom: 20rem;
}

.modal-pricing {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.pricing-row {
  display: flex;
  align-items: baseline;
  padding: 12rem 0;
  border-bottom: 1rem solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.pricing-row--sub {
  padding-left: 20rem;

  .pricing-label {
    font-size: 14rem;
    color: #666;
  }

  .pricing-price {
    font-size: 14rem;
    font-weight: 600;
  }
}

.pricing-label {
  font-size: 15rem;
  color: #333;
}

.pricing-dots {
  flex: 1;
  border-bottom: 2rem dotted #d0d0d0;
  margin: 0 12rem;
  min-width: 20rem;
}

.pricing-price {
  font-size: 15rem;
  font-weight: 700;
  color: var(--primary);
  white-space: nowrap;
}

.pricing-affix {
  font-weight: 400;
  opacity: 0.75;
}

.pricing-vat {
  margin-top: 12rem;
  font-size: 12rem;
  opacity: 0.6;
  text-align: right;
}

/* Calculator */
.pricing-calc-btn {
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  font-size: 13rem;
  padding: 4rem 8rem;
  margin-right: 8rem;
  border-radius: 4rem;
  opacity: 0.6;
  transition: opacity 0.2s, background 0.2s;

  &:hover,
  &.active {
    opacity: 1;
    background: rgba(var(--accent-rgb, 0, 0, 0), 0.06);
  }
}

.pricing-calc {
  display: flex;
  align-items: center;
  gap: 10rem;
  padding: 8rem 0 8rem 16rem;
  border-bottom: 1rem solid #c0cdd4;
}

.pricing-calc__label {
  font-size: 13rem;
  color: #c0cdd4;
  white-space: nowrap;
}

.pricing-calc__input-wrap {
  display: flex;
  align-items: center;
  border: 1rem solid var(--accent);
  border-radius: 6rem;
  overflow: hidden;
}

.pricing-calc__step {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16rem;
  padding: 4rem 10rem;
  line-height: 1;
  color: var(--accent);
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: rgba(var(--accent-rgb, 0, 0, 0), 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.pricing-calc__input {
  width: 48rem;

  :deep(.el-input__wrapper) {
    box-shadow: none;
    border: none;
    border-radius: 0;
    border-left: 1rem solid var(--accent);
    border-right: 1rem solid var(--accent);
    padding: 0;
    height: 28rem;
    background: transparent;
  }

  :deep(.el-input__inner) {
    text-align: center;
    font-size: 14rem;
    font-weight: 600;
    color: var(--accent);
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.pricing-calc__result {
  font-size: 14rem;
  font-weight: 700;
  color: var(--accent);
  white-space: nowrap;
}
</style>
